/**
 * Buyer-side: list of training sessions I've booked at `/me/sessions`.
 * Each row links to /sessions/:id/join (room opens 5 min before start).
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { VideoCameraIcon as VideoCamera, ArrowRightIcon as ArrowRight } from "@phosphor-icons/react";

const $ = (c) => `$${((c || 0) / 100).toFixed(2)}`;
const fmt = (iso) => iso ? new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

export default function MySessions() {
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/trainer-sessions/me/buyer")
      .then((r) => setList(r.data?.sessions || []))
      .catch(() => setList([]));
  }, []);

  return (
    <AppShell>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 md:px-10 py-8 font-manrope text-ink">
        <p className="text-xs uppercase tracking-[0.2em] text-gold font-bold">My training</p>
        <h1 className="font-spectral text-3xl sm:text-4xl tracking-tight mt-1">Live sessions</h1>
        <p className="text-sm text-[#666] max-w-2xl mt-2">
          Every live training session you've booked. The join button appears 5 minutes before each scheduled start.
        </p>

        {list === null ? (
          <div className="mt-6 space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20" />)}</div>
        ) : list.length === 0 ? (
          <div className="mt-6 bg-white border border-[#E5E5E5] rounded-2xl p-10 text-center" data-testid="my-sessions-empty">
            <VideoCamera size={36} className="mx-auto text-[#9b9b9b]" />
            <p className="mt-3 font-spectral text-xl">No sessions booked yet.</p>
            <p className="text-sm text-[#666] mt-1 max-w-md mx-auto">
              Browse our verified trainers and book a 1-on-1 live video session.
            </p>
            <Link
              to="/find-pros"
              className="inline-flex items-center gap-1.5 mt-5 rounded-full bg-[#0E0E0E] hover:bg-[#C1272D] text-white px-5 py-2 text-sm font-semibold transition-colors"
              data-testid="my-sessions-find-trainer"
            >
              Find a trainer <ArrowRight size={14} />
            </Link>
          </div>
        ) : (
          <ul className="mt-6 space-y-3" data-testid="my-sessions-list">
            {list.map((s) => {
              const canJoin = s.status === "confirmed" && (new Date(s.scheduled_at) - Date.now()) < 5 * 60 * 1000;
              return (
                <li key={s.id} className="bg-white border border-[#E5E5E5] rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid={`my-session-${s.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-spectral text-base">{s.tier_label}</p>
                    <p className="text-xs text-[#888]">{fmt(s.scheduled_at)} · {s.duration_min} min · {$(s.gross_cents)} · {s.status}</p>
                  </div>
                  {canJoin ? (
                    <Link to={`/sessions/${s.id}/join`} className="text-sm font-semibold bg-[#0E0E0E] text-white rounded-full px-4 py-1.5 hover:bg-[#C1272D]" data-testid={`my-session-join-${s.id}`}>
                      Join now →
                    </Link>
                  ) : s.status === "confirmed" ? (
                    <div className="flex items-center gap-2">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/trainer-sessions/${s.id}/calendar.ics`}
                        className="text-[11px] underline text-[#666] hover:text-ink"
                        data-testid={`my-session-ics-${s.id}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        Add to calendar
                      </a>
                      <span className="text-xs text-[#888]">Opens 5 min before</span>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </AppShell>
  );
}
